import { useExamCenter } from '@exam-center/exam-center-details';
import {
  ExamLocationRequestForm,
  ExamLocationRequestFormProvider,
  type ExamLocationRequestFormSubmitInput,
  type ExamLocationRequestFormSubmitOptions,
  ExamLocationRequestRoomSection,
  ExamLocationRequestSubmitErrorAlert,
  useExamLocationRequestDetails,
  useExamLocationRequestForm,
} from '@exam-center/exam-location-management';
import {
  useUpdateExamLocationRequestInfoMutation,
  useSubmitExamLocationRequestInfoMutation,
  type SubmitExamLocationRequestInfoMutation,
  GetExamLocationRequestInfoDocument,
  SearchExamLocationRequestInfosDocument,
  useDeleteExamLocationRequestInfoByIdMutation,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate, useParams } from '@exam-center/router';
import { FormButton, Form, Flex, Button, ConfirmationModal, Text } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useState } from 'react';

function ExamLocationRequestUpdateActions({
  onSubmitExamLocationRequest,
  enableSubmit,
  isRequestSubmitting,
}: {
  onSubmitExamLocationRequest: () => Promise<void>;
  enableSubmit: boolean;
  isRequestSubmitting: boolean;
}) {
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();
  const { examLocationRequestInfo } = useExamLocationRequestDetails();
  const { submitting, isUpdateMode, submit } = useExamLocationRequestForm();
  const [deleteExamLocationRequest, { loading }] = useDeleteExamLocationRequestInfoByIdMutation();

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { examCenter } = useExamCenter();

  const handleDeleteExamLocationRequest = async () => {
    await deleteExamLocationRequest({
      variables: {
        deleteExamLocationRequestInfoByIdInput: {
          id: examLocationRequestInfo.id,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'ลบแบบร่างสำเร็จ',
          variant: 'success',
        });
        navigate('/:examCenterCode/exam-location/requests', {
          params: { examCenterCode: examCenter.code },
        });
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการลบแบบร่าง กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [SearchExamLocationRequestInfosDocument],
    });
  };

  if (!isUpdateMode) return null;
  return (
    <>
      <Flex
        sx={{
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <WithPermission
          requiredPermission={[
            APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.DELETE,
          ]}
          errorElement={Flex}
        >
          <Button size='large' onClick={() => openDeleteModal()} color='error'>
            ลบแบบร่าง
          </Button>
        </WithPermission>
        <Flex
          sx={{
            gap: 2,
          }}
        >
          <FormButton
            size='large'
            label='บันทึกข้อมูล'
            type='button'
            variant='outlined'
            onClick={() => submit()}
            loading={submitting}
          />
          <Button
            size='large'
            onClick={async () => {
              try {
                await submit({ showSuccessToast: false });
                await onSubmitExamLocationRequest();
              } catch {
                // Error toast is already shown by handleSubmit
              }
            }}
            disabled={!enableSubmit}
            loading={isRequestSubmitting}
          >
            ส่งคำขออนุมัติ
          </Button>
        </Flex>
      </Flex>
      <ConfirmationModal
        title='ลบแบบร่าง?'
        content={
          <Text>
            คุณกำลังจะลบแบบร่างคำขออนุมัติสถานที่ แบบร่างนี้จะถูกลบถาวรและไม่สามารถนำกลับมาได้อีก
          </Text>
        }
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onDiscard={closeDeleteModal}
        onAccept={handleDeleteExamLocationRequest}
        acceptText='ลบแบบร่าง'
        discardText='ยกเลิก'
        acceptButtonProps={{ color: 'error' }}
        loading={loading}
      />
    </>
  );
}

function ExamLocationRequestFormWrapper() {
  const { submit } = useExamLocationRequestForm();
  return (
    <Form
      onSubmit={async (e) => {
        e.preventDefault();
        await submit();
      }}
      sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) })}
    >
      <ExamLocationRequestForm />
    </Form>
  );
}

export default function ExamLocationRequestDetailsPage() {
  const { examCenterCode, requestId } = useParams(
    '/:examCenterCode/exam-location/requests/draft/:requestId',
  );
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    examLocationRequestInfo: info,
    roomIndexMap,
    sortedExamRooms,
  } = useExamLocationRequestDetails();
  const [updateExamLocationRequestInfo] = useUpdateExamLocationRequestInfoMutation();
  const [submitExamLocationRequest, { loading: isRequestSubmitting }] =
    useSubmitExamLocationRequestInfoMutation();
  const [submitExamLocationRequestResponse, setSubmitExamLocationRequestResponse] =
    useState<SubmitExamLocationRequestInfoMutation | null>(null);

  const initialValues = {
    provinceId: info.provinceId,
    locationId: info.locationId,
    locationNameTh: info.nameTh,
    locationNameEn: info.nameEn,
    buildingId: info.buildingId,
    buildingName: info.buildingName,
  };

  const handleSubmit = async (
    input: ExamLocationRequestFormSubmitInput,
    options?: ExamLocationRequestFormSubmitOptions,
  ) => {
    const showSuccessToast = options?.showSuccessToast ?? true;

    try {
      await updateExamLocationRequestInfo({
        variables: {
          updateExamLocationRequestInfoInput: {
            id: info.id,
            locationId: input.locationId ?? null,
            provinceId: input.provinceId ?? null,
            nameTh: input.locationNameTh?.trim(),
            nameEn: input.locationNameEn?.trim() || null,
            buildingId: input.buildingId ?? null,
            buildingName: input.buildingId ? null : (input.buildingName?.trim() ?? null),
          },
        },
        onCompleted: () => {
          if (showSuccessToast) {
            showSnackbar({
              message: 'บันทึกข้อมูลเรียบร้อยแล้ว',
              variant: 'success',
            });
          }
        },
        refetchQueries: [
          GetExamLocationRequestInfoDocument,
          SearchExamLocationRequestInfosDocument,
        ],
      });
    } catch (error) {
      showSnackbar({ message: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง', variant: 'error' });
      throw error; // Re-throw to propagate to caller
    }
  };

  const handleSubmitExamLocationRequest = async () => {
    setSubmitExamLocationRequestResponse(null);
    await submitExamLocationRequest({
      variables: { submitExamLocationRequestInfoInput: { id: info.id } },
      onCompleted: (res) => {
        if (res.ecSubmitExamLocationRequestInfo.isSuccess) {
          showSnackbar({ message: 'ส่งคำขออนุมัติเรียบร้อยแล้ว', variant: 'success' });
          navigate('/:examCenterCode/exam-location/requests', {
            params: { examCenterCode },
          });
          return;
        } else {
          showSnackbar({
            message: 'ไม่สามารถส่งคำขออนุมัติได้ กรุณากรอกข้อมูลให้ครบถ้วน',
            variant: 'error',
          });
          setSubmitExamLocationRequestResponse(res);
        }
      },
      onError: () => {
        showSnackbar({ message: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง', variant: 'error' });
      },
      refetchQueries: [GetExamLocationRequestInfoDocument, SearchExamLocationRequestInfosDocument],
    });
  };

  // redirect to request list if any exam room has progressed beyond draft
  useEffect(() => {
    if (sortedExamRooms.some((room) => room.step !== null)) {
      navigate(`/:examCenterCode/exam-location/requests`, {
        params: { examCenterCode },
        replace: true,
      });
    }
  }, [sortedExamRooms, navigate, examCenterCode]);

  // Clear error alert when room data changes (after delete/add)
  useEffect(() => {
    if (submitExamLocationRequestResponse) {
      setSubmitExamLocationRequestResponse(null);
    }
  }, [sortedExamRooms.length]);

  // scroll to top when error alert appears
  useEffect(() => {
    if (
      submitExamLocationRequestResponse &&
      !submitExamLocationRequestResponse.ecSubmitExamLocationRequestInfo.isSuccess
    ) {
      const frameContent = document.getElementById('frame-content');
      if (frameContent) {
        frameContent.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }, [submitExamLocationRequestResponse]);

  return (
    <ExamLocationRequestFormProvider
      onSubmit={handleSubmit}
      initialValues={initialValues}
      withFormElement={false}
    >
      <>
        <ExamLocationRequestSubmitErrorAlert
          examCenterCode={examCenterCode}
          requestId={requestId}
          submitExamLocationRequestResponse={submitExamLocationRequestResponse}
          roomIndexMap={roomIndexMap}
        />
        <ExamLocationRequestFormWrapper />
        <ExamLocationRequestRoomSection />

        {/* Hide Update Buttons if user does not have update permission */}
        <WithPermission
          requiredPermission={[
            APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
          ]}
        >
          <ExamLocationRequestUpdateActions
            onSubmitExamLocationRequest={handleSubmitExamLocationRequest}
            enableSubmit={sortedExamRooms.length > 0}
            isRequestSubmitting={isRequestSubmitting}
          />
        </WithPermission>
      </>
    </ExamLocationRequestFormProvider>
  );
}
